import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { SelectV4 } from '@labrav/react-components'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { SelectChangeEvent } from '@mui/material'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import {
  isLoading,
  LoadingTypes,
  useCurrentCloudProvider,
} from '@labrav/common'

import { useDispatch, useSelector } from '../../../store/hooks'
import { FlyoutFunction } from '../../../modules/productsListing/types'
import {
  getFlyoutProductsListBasedOnFunction,
  updateProductIdOnFilters,
} from '../../../modules/productListingFilter/action'
import type { AccountData } from '../../../types/external'

export interface ProductSelectorDropdownProps {
  functionType: FlyoutFunction
  externalBaseURL?: string
  search?: string
  mediumSize?: boolean
}

export const ProductSelectorDropdown: React.FC<
  ProductSelectorDropdownProps
> = ({ functionType, externalBaseURL, search, mediumSize }) => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const cloudMarketplace = useCurrentCloudProvider()
  const productsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )
  const { selectedProduct: productId, products } =
    useFlyOutProductsBasedOnSelectedCloud()

  const { partnerData } = useSelector<{
    partnerData: undefined | AccountData
  }>(state => ({
    partnerData: state.AccountData.user?.partnerData || undefined,
  }))

  const baseURL = useMemo(() => {
    if (externalBaseURL) {
      return externalBaseURL
        ?.split('product-listing')[0]
        ?.concat('product-listing')
    }
    return '/flyout'
  }, [externalBaseURL])

  const handleProductChange = useCallback(
    async (event: SelectChangeEvent) => {
      const product = event.target.value as string
      const actualProductId = product === 'allProducts' ? '' : product
      await dispatch(
        updateProductIdOnFilters(
          actualProductId,
          cloudMarketplace,
          functionType
        )
      )
    },
    [baseURL, search, functionType, pathname, cloudMarketplace, dispatch]
  )

  useEffect(() => {
    if (!productsLoading && productId === undefined && partnerData?.partnerId) {
      const partnershipType = partnerData?.partnershipType
      const isCPUser = partnershipType?.includes('CONSULTING_PARTNER')
      const isOnlyISV =
        partnershipType?.length === 1 &&
        partnershipType?.includes('TECHNOLOGY_PARTNER')
      const isProductOnlyOneAvailable =
        !isEmpty(products) && products?.length === 1
      const updatedProductId =
        isCPUser && isEmpty(products)
          ? ''
          : isProductOnlyOneAvailable
            ? products[0].productId
            : ''
      const shouldUpdateProductId =
        isCPUser || (isOnlyISV && !isEmpty(products))
      if (shouldUpdateProductId) {
        dispatch(
          updateProductIdOnFilters(
            updatedProductId,
            cloudMarketplace,
            functionType
          )
        )
      }
    }
  }, [
    products,
    productsLoading,
    partnerData,
    dispatch,
    cloudMarketplace,
    functionType,
    productId,
  ])

  const productsForFilters = getFlyoutProductsListBasedOnFunction(
    products,
    functionType,
    cloudMarketplace
  )

  const defaultSelectedProduct = useMemo(() => {
    return isEmpty(productId) ? 'allProducts' : productId
  }, [productId])

  const selectV2Size = mediumSize ? 'medium' : 'small'

  return (
    <SelectV4
      field={{
        title: 'Select a product',
        placeHolder: 'Select a product',
        id: 'product-selector-dropdown',
        isRequired: false,
        humanReadableKey: 'selectProduct',
        isReadOnly: false,
        isCustom: false,
        defaultValue: defaultSelectedProduct,
        value: defaultSelectedProduct,
        dataType: 'enum' as DataType,
        options: productsForFilters,
      }}
      onChange={handleProductChange}
      isError={false}
      {...(selectV2Size ? { size: selectV2Size } : {})}
      minWidth="192px"
    />
  )
}
